/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal, ViewEncapsulation } from '@angular/core';
import {
    CurriculumPeriod,
    CustomModuleAttainment,
    DegreeProgramme,
    GroupingModule,
    Module,
    ModuleResultItem,
    StudyModule,
} from 'common-typescript/types';
import { map, Observable, switchMap } from 'rxjs';
import { LocaleService } from 'sis-common';
import {
    AppErrorHandler,
    filterInput,
    isGroupingModule,
    isStudyModule,
    ModuleEntityService,
    PlanStateObject,
    PlanStructureContext,
    UniversityService,
} from 'sis-components';
import { UnplannedAttainments } from '../../../../../../plan-ng/plan-data.service';

@Component({
    selector: 'app-free-edit-module-expandable',
    templateUrl: './free-edit-module-expandable.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FreeEditModuleExpandableComponent implements OnInit {
    protected readonly planStructureContext = inject(PlanStructureContext);
    searchCurriculumPeriods = input<CurriculumPeriod[]>([]);
    /**
     * All modules that should be toggleable and listed under the search
     */
    allModules = input<(GroupingModule | StudyModule | DegreeProgramme)[]>([]);
    planStateObject = input<PlanStateObject>();
    unplannedAttainments = input<UnplannedAttainments>();
    allCustomModuleAttainments = input<CustomModuleAttainment[]>([]);

    searchCurriculumPeriodIds = computed(() => this.searchCurriculumPeriods().map(cur => cur.id));
    allModulesIdSet = computed(() => new Set(this.allModules().map(mod => mod.id)));

    readonly selectedModules = computed(() => {
        const savedModuleIds = new Set(this.savedModules().map(({ id }) => id));
        const attainedModuleIds = new Set(this.unplannedModuleAttainments().map(({ moduleId }) => moduleId));
        return this.allModules().filter(({ id }) => !savedModuleIds.has(id) && !attainedModuleIds.has(id));
    });
    readonly savedModules = signal<Module[]>([]);
    readonly savedCustomModuleAttainments = signal<CustomModuleAttainment[]>([]);
    readonly unplannedModuleAttainments = computed(() => this.unplannedAttainments()?.moduleAttainments ?? []);
    readonly unplannedCustomModuleAttainments = computed(() => this.unplannedAttainments()?.customModuleAttainments ?? []);

    /**
     * Emits a module selection that has been toggled
     */
    toggleModule = output<{
        module: GroupingModule | StudyModule | DegreeProgramme;
        isInPlan: boolean;
    }>();
    /**
     * Emits a custom module attainment selection that has been toggled
     */
    toggleCustomModule = output<{
        customModuleAttainment: CustomModuleAttainment;
        isInPlan: boolean;
    }>();
    /**
     * Emits a newly selected module
     */
    selectModule = output<GroupingModule | StudyModule | DegreeProgramme>();

    private moduleEntityService = inject(ModuleEntityService);
    private localeService = inject(LocaleService);
    private universityService = inject(UniversityService);
    private appErrorHandler = inject(AppErrorHandler);

    readonly isGroupingModule = isGroupingModule;
    readonly isStudyModule = isStudyModule;

    ngOnInit(): void {
        const plannedModuleIds = new Set(Object.keys(this.planStructureContext.planData()?.modulesById ?? {}));
        this.savedModules.set(this.allModules().filter(mod => plannedModuleIds.has(mod.id)));

        const plannedCustomModuleAttainmentIds = new Set(Object.keys(this.planStructureContext.planData()?.attainmentsById ?? {}));
        this.savedCustomModuleAttainments.set(
            this.allCustomModuleAttainments().filter(att => plannedCustomModuleAttainmentIds.has(att.id)),
        );
    }

    doSearch(query$: Observable<string>) {
        return query$.pipe(
            filterInput(),
            switchMap(searchText =>
                this.moduleEntityService.searchUnauthenticated({
                    fullTextQuery: searchText,
                    uiLang: this.localeService.getCurrentLanguage(),
                    start: 0,
                    limit: 100,
                    validity: 'ONGOING_AND_FUTURE',
                    curriculumPeriodIds: this.searchCurriculumPeriodIds(),
                    type: 'StudyModule',
                    organisationRootIds: [this.universityService.getCurrentUniversityOrgId()],
                }),
            ),
            map(result =>
                result.searchResults.filter(
                    (mod: ModuleResultItem) =>
                        // TODO: instead of filtering, these should be shown as disabled. This is temporary until typeahead can
                        // be replaced with a equivalent fudis component.
                        !this.planStructureContext.validatablePlan().isModuleInPlan(mod.id) && !this.allModulesIdSet().has(mod.id),
                ),
            ),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }
}
